import { Code, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import type { UseRowSelectionReturn } from '../../../hooks/useRowSelection';
import { mutate } from '../../../util/api/mutate';
import { useBulkUpsertProductPriceForm } from '../hooks/useBulkUpsertPriceForm';
import type {
	BranchSkuidPairDto,
	BulkUpsertProductPriceDto,
} from '../types/ProductPriceDto';
import { BulkUpdateProductPriceForm } from './BulkUpdateProductPriceForm';

interface BulkUpdateProductPriceModalProps {
	close: () => void;
	isOpened: boolean;
	onSuccess: () => void;
	rowSelection: UseRowSelectionReturn<BranchSkuidPairDto>;
	urlParams: string;
}

export function BulkUpdateProductPriceModal({
	isOpened,
	close,
	onSuccess,
	rowSelection,
	urlParams,
}: BulkUpdateProductPriceModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useBulkUpsertProductPriceForm();

	const bulkProductPriceMutation = mutate<BulkUpsertProductPriceDto, number>(
		`${organisation}/product-price/edit/bulk?${urlParams}`,
		{
			...inputs,
			keys: rowSelection.selectedKeys,
			allSelected: rowSelection.allSelectedMode,
		},
	);

	const resetAndClose = () => {
		form.reset();

		close();
		closeSuccessModal();
	};

	const complete = () => {
		onSuccess();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={
					<>
						<Title label={'Bulk Edit Product Prices'} />
						<Code>
							{`${rowSelection.selectedCount} item${rowSelection.selectedCount === 1 ? '' : 's'} selected. `}
						</Code>
					</>
				}
				withCloseButton={true}
				closeButtonProps={{
					style: { alignSelf: 'flex-start' },
				}}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={bulkProductPriceMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Product Prices Edited!'}
					body={`${rowSelection.selectedCount} item${rowSelection.selectedCount === 1 ? ' has' : 's have'} been edited. Click Ok to continue...`}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await bulkProductPriceMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<BulkUpdateProductPriceForm form={form} />
					<FormButtons
						close={resetAndClose}
						confirmModalProps={{
							title: 'Confirm bulk price update',
							body: (
								<>
									Update price and stock level for {rowSelection.selectedCount}{' '}
									item
									{rowSelection.selectedCount === 1 ? '' : 's'}? Click OK to
									confirm...
								</>
							),
						}}
					/>
				</form>
			</Modal>
		</>
	);
}
